import type { EffectReducer } from "use-effect-reducer";
import type { Action, AppState } from "./actions.ts";
import type { DrawObject, Point } from "../types/document.ts";

export type AppEffect = { type: "exportJson" } | { type: "exportPng" };

export const initialState: AppState = {
  objects: [],
  textHtml: "",
  textStyle: {
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: 16,
    color: "#5D4037",
    lineHeight: 1.5,
    fontWeight: "bold",
  },
  activeTool: "text",
  activeColor: "#1a1a1a",
  activeWidth: 2,
  activeFill: undefined,
  activeEmoji: undefined,
  activeEmojiSize: 32,
  inProgress: null,
  drawStartPoint: null,
  undoStack: [],
  redoStack: [],
  canvasWidth: 800,
  canvasHeight: 600,
};

function createInProgress(state: AppState, point: Point): DrawObject | null {
  const { activeTool, activeColor, activeWidth, activeFill } = state;
  switch (activeTool) {
    case "pencil":
      return { type: "pencil", points: [point], color: activeColor, width: activeWidth };
    case "line":
      return { type: "line", from: point, to: point, color: activeColor, width: activeWidth };
    case "rect":
      return {
        type: "rect",
        origin: point,
        size: [0, 0],
        color: activeColor,
        width: activeWidth,
        fill: activeFill,
      };
    case "ellipse":
      return {
        type: "ellipse",
        center: point,
        radii: [0, 0],
        color: activeColor,
        width: activeWidth,
        fill: activeFill,
      };
    default:
      return null;
  }
}

function updateInProgress(obj: DrawObject, point: Point, startPoint: Point): DrawObject {
  switch (obj.type) {
    case "pencil":
      return { ...obj, points: [...obj.points, point] };
    case "line":
      return { ...obj, to: point };
    case "rect": {
      const origin: Point = [Math.min(startPoint[0], point[0]), Math.min(startPoint[1], point[1])];
      const size: Point = [Math.abs(point[0] - startPoint[0]), Math.abs(point[1] - startPoint[1])];
      return { ...obj, origin, size };
    }
    case "ellipse": {
      const center: Point = [(startPoint[0] + point[0]) / 2, (startPoint[1] + point[1]) / 2];
      const radii: Point = [
        Math.abs(point[0] - startPoint[0]) / 2,
        Math.abs(point[1] - startPoint[1]) / 2,
      ];
      return { ...obj, center, radii };
    }
    default:
      return obj;
  }
}

export const reducer: EffectReducer<AppState, Action, AppEffect> = (state, action, exec) => {
  switch (action.type) {
    case "DRAW_START": {
      const obj = createInProgress(state, action.point);
      return obj ? { ...state, inProgress: obj, drawStartPoint: action.point } : state;
    }
    case "DRAW_MOVE": {
      if (!state.inProgress || !state.drawStartPoint) return state;
      return {
        ...state,
        inProgress: updateInProgress(state.inProgress, action.point, state.drawStartPoint),
      };
    }
    case "DRAW_END": {
      if (!state.inProgress) return state;
      return {
        ...state,
        objects: [...state.objects, state.inProgress],
        inProgress: null,
        drawStartPoint: null,
        undoStack: [...state.undoStack, state.objects],
        redoStack: [],
      };
    }
    case "STAMP_EMOJI": {
      if (!state.activeEmoji) return state;
      const emojiObj: DrawObject = {
        type: "emoji",
        position: action.point,
        emoji: state.activeEmoji,
        size: state.activeEmojiSize,
      };
      return {
        ...state,
        objects: [...state.objects, emojiObj],
        undoStack: [...state.undoStack, state.objects],
        redoStack: [],
      };
    }
    case "SET_TOOL":
      return { ...state, activeTool: action.tool };
    case "SET_COLOR":
      return { ...state, activeColor: action.color };
    case "SET_WIDTH":
      return { ...state, activeWidth: action.width };
    case "SET_FILL":
      return { ...state, activeFill: action.fill };
    case "SET_EMOJI":
      return { ...state, activeEmoji: action.emoji };
    case "SET_EMOJI_SIZE":
      return { ...state, activeEmojiSize: action.size };
    case "SET_TEXT_HTML":
      return { ...state, textHtml: action.html };
    case "SET_TEXT_STYLE":
      return { ...state, textStyle: { ...state.textStyle, ...action.style } };
    case "UNDO": {
      if (state.undoStack.length === 0) return state;
      const prev = state.undoStack[state.undoStack.length - 1];
      return {
        ...state,
        objects: prev,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, state.objects],
      };
    }
    case "REDO": {
      if (state.redoStack.length === 0) return state;
      const next = state.redoStack[state.redoStack.length - 1];
      return {
        ...state,
        objects: next,
        redoStack: state.redoStack.slice(0, -1),
        undoStack: [...state.undoStack, state.objects],
      };
    }
    case "EXPORT_JSON":
      exec({ type: "exportJson" });
      return state;
    case "EXPORT_PNG":
      exec({ type: "exportPng" });
      return state;
    case "IMPORT_JSON":
      return {
        ...state,
        objects: action.document.objects,
        textHtml: action.document.text.html,
        textStyle: action.document.text.style,
        canvasWidth: action.document.canvas.width,
        canvasHeight: action.document.canvas.height,
        undoStack: [],
        redoStack: [],
        inProgress: null,
        drawStartPoint: null,
      };
    case "CLEAR_CANVAS":
      return {
        ...state,
        objects: [],
        textHtml: "",
        undoStack: [...state.undoStack, state.objects],
        redoStack: [],
        inProgress: null,
        drawStartPoint: null,
      };
    case "ADD_OBJECT":
      return {
        ...state,
        objects: [...state.objects, action.object],
        undoStack: [...state.undoStack, state.objects],
        redoStack: [],
      };
    case "CLEAR_OBJECTS":
      return {
        ...state,
        objects: [],
        undoStack: [...state.undoStack, state.objects],
        redoStack: [],
        inProgress: null,
        drawStartPoint: null,
      };
    default:
      return state;
  }
};
